import React, { useEffect } from 'lib/react';
import {
  Switch, Route,
  Redirect,
} from 'lib/router';
import {
  EVTabs, EVTab, EVPaper, EVGrid,
} from 'lib/components';
import {
  ORDER_PREFERENCES_ROUTE, ORDER_PREFERENCES_PRODUCT_SELECTION_ROUTE,
  ORDER_PREFERENCES_PRODUCT_SUBSTITUTIONS_ROUTE, ORDER_PREFERENCES_PRODUCT_ADDONS_ROUTE, ORDER_PREFERENCES_CUSTOM_COVER_PAGE_ROUTE,
  ORDER_PREFERENCES_TRUDESIGN_SETTINGS_ROUTE,
} from 'constants/RouteConstants';
import history from 'store/CreateHistory';
import { useSelector } from 'react-redux';
import { useDispatch } from 'lib/redux';
import { isObjectEmpty } from 'utils/app.utils';
import Banner from 'commonComponents/banner';
import { isOrderingDisabledSelector } from 'utils/appSelectors.utils';
import AccessDeniedPage from 'commonComponents/accessDeniedPage/accessDeniedPage.component';
import OrderPreferenceRetry from './OrderPreferenceRetry';
import OrderPreferencesStyles from './OrderPreferencesStyles';
import ProductSelection from './components/productSelection/ProductSelection.component';
import ProductSubstitutions from './components/productSubstitutions/ProductSubstitutions.components';
import ProductAddons from './components/productAddons/ProductAddons.component';
import CustomCover from './components/customCover/CustomCover.component';
import TrueDesignSettings from './components/TrueDesignSettings.component';
import { getTabListItems } from './OrderPreferences.utils';
import { cancelOrderPreferencesChanges, closeOrderPreferencesAlert, loadOrderPreferencePage } from './OrderPreferences.actions';
import SaveAndCancelButtons from './components/SaveAndCancelButtons.component';
import OrderPreferenceLoader from './OrderPreferenceLoader';

const redirectComponent = () => <Redirect to={ORDER_PREFERENCES_PRODUCT_SELECTION_ROUTE} />;

function OrderPreferences() {
  const dispatch = useDispatch();
  const classes = OrderPreferencesStyles();

  const {
    orderPreferencesLoading, orderPreferencesError, orderPreferences,
    orderPreferencesAlert, userProductsLoading, userProductsError,
  } = useSelector(({ userData, orderPreference }) => ({
    orderPreferencesLoading: userData.loading?.orderPreferences,
    userProductsLoading: userData.loading?.userProducts,
    orderPreferencesError: userData.error?.orderPreferences,
    orderPreferences: userData.orderPreferences,
    updatedValues: orderPreference.updatedValues,
    orderPreferencesAlert: orderPreference.orderPreferencesAlert,
    userProductsError: userData.error?.userProducts,
  }));

  const isOrderingDisabled = useSelector(isOrderingDisabledSelector);

  const tabListItems = getTabListItems();

  useEffect(() => dispatch(loadOrderPreferencePage()), []);

  useEffect(() => () => dispatch((cancelOrderPreferencesChanges())), [history.location.pathname]);

  const handleChange = (_, newValue) => {
    history.push(newValue);
  };

  const handleAlertClose = () => dispatch(closeOrderPreferencesAlert());

  if (isOrderingDisabled) {
    return (
      <AccessDeniedPage />
    );
  }

  if (orderPreferencesError) {
    return (
      <OrderPreferenceRetry handleRetry={() => {
        dispatch(loadOrderPreferencePage());
      }}
      />
    );
  }

  if (isObjectEmpty(orderPreferences) || orderPreferencesLoading || userProductsLoading) return <OrderPreferenceLoader />;
  const renderPage = () => (
    <>
      <Switch>
        <Route exact path={ORDER_PREFERENCES_PRODUCT_SELECTION_ROUTE} component={ProductSelection} />
        <Route exact path={ORDER_PREFERENCES_PRODUCT_SUBSTITUTIONS_ROUTE} component={ProductSubstitutions} />
        <Route exact path={ORDER_PREFERENCES_PRODUCT_ADDONS_ROUTE} component={ProductAddons} />
        <Route exact path={ORDER_PREFERENCES_CUSTOM_COVER_PAGE_ROUTE} component={CustomCover} />
        <Route exact path={ORDER_PREFERENCES_TRUDESIGN_SETTINGS_ROUTE} component={TrueDesignSettings} />
        <Route
          exact
          path={ORDER_PREFERENCES_ROUTE}
          component={redirectComponent}
        />
      </Switch>
      {!userProductsError && <SaveAndCancelButtons />}
    </>
  );

  return (
    <>
      {orderPreferencesAlert.open && (
        <Banner
          severity={orderPreferencesAlert.severity}
          onClose={handleAlertClose}
          message={
            (typeof orderPreferencesAlert.message === 'object')
              ? Object.values(orderPreferencesAlert.message).map((errorMsg) => errorMsg)
              : (orderPreferencesAlert.message)
          }
        />
      )}
      <EVGrid container className={classes.orderPreferencesContainer}>
        <EVPaper className={classes.orderPreferencesPaper}>
          <EVGrid className={classes.tabsContainer}>
            <EVTabs
              orientation="vertical"
              variant="scrollable"
              value={history.location.pathname}
              onChange={handleChange}
              aria-label="tab-list"
              className={classes.tabList}
              classes={{ indicator: classes.indicator }}
            >
              {tabListItems.map((item) => (
                <EVTab
                  key={item.id}
                  data-testid={`tab-list-item-${item.id}`}
                  className={classes.tabElement}
                  label={item.label}
                  value={item.url}
                />
              ))}
            </EVTabs>
            <EVGrid className={classes.fillerGrid} />
          </EVGrid>
          <EVGrid style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
            {renderPage()}
          </EVGrid>
        </EVPaper>
      </EVGrid>
    </>
  );
}

export default OrderPreferences;
